/**
 * Push Notifications Utility für Hayat Family
 * 
 * Nutzt Firebase Cloud Messaging (FCM) für Browser-Push
 * 
 * Flow:
 * 1. Service Worker registrieren
 * 2. Permission anfragen
 * 3. FCM Token generieren
 * 4. Token an Backend senden
 */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// FCM Config (aus ENV laden)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FCM_API_KEY,
  authDomain: process.env.REACT_APP_FCM_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FCM_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FCM_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FCM_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FCM_APP_ID
};

// VAPID Public Key für Web Push
const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY;

let firebaseApp = null;
let messaging = null;

/**
 * Initialisiere Firebase
 */
export function initializeFirebase() {
  if (firebaseApp) return firebaseApp;
  
  try {
    // Check if config is available
    if (!firebaseConfig.apiKey || !VAPID_KEY) {
      console.warn('[Push] FCM config not available. Push notifications will not work.');
      console.warn('[Push] Please set REACT_APP_FCM_* environment variables');
      return null;
    }
    
    firebaseApp = initializeApp(firebaseConfig);
    messaging = getMessaging(firebaseApp);
    console.log('[Push] Firebase initialized successfully');
    return firebaseApp;
  } catch (error) {
    console.error('[Push] Firebase initialization error:', error);
    return null;
  }
}

/**
 * Detect if running on iOS Safari
 */
export function isIOSSafari() {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua);
  const notChrome = !/CriOS|Chrome/.test(ua);
  return iOS && webkit && notChrome;
}

/**
 * Check if Push Notifications are supported
 * Returns: true, false, or 'ios-not-supported'
 */
export function isPushSupported() {
  // iOS Safari < 16.4 doesn't support Push API
  if (isIOSSafari()) {
    // Check if Push API is actually available
    if (!('Notification' in window) || !('PushManager' in window)) {
      return 'ios-not-supported';
    }
  }
  
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Get current notification permission status
 */
export function getPermissionStatus() {
  if (!isPushSupported()) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', 'default'
}

/**
 * Request notification permission
 */
export async function requestPermission() {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported in this browser');
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission === 'denied') {
    throw new Error('Notification permission denied. Please enable in browser settings.');
  }
  
  try {
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission:', permission);
    return permission;
  } catch (error) {
    console.error('[Push] Permission request error:', error);
    throw new Error('Failed to request notification permission');
  }
}

/**
 * Get FCM Push Token
 * 
 * @returns {Promise<string>} Push token
 */
export async function getPushToken() {
  // Initialize Firebase if not already done
  if (!firebaseApp) {
    initializeFirebase();
  }
  
  if (!messaging) {
    throw new Error('Firebase Messaging not initialized. Check FCM configuration.');
  }
  
  try {
    // Request permission first
    const permission = await requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }
    
    // Get Service Worker registration
    const registration = await navigator.serviceWorker.ready;
    
    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    
    if (!token) {
      throw new Error('Failed to generate push token');
    }
    
    console.log('[Push] Token generated:', token.substring(0, 20) + '...');
    return token;
  } catch (error) {
    console.error('[Push] Get token error:', error);
    throw error;
  }
}

/**
 * Listen for foreground messages
 * (messages received while app is open)
 */
export function onForegroundMessage(callback) {
  if (!messaging) {
    console.warn('[Push] Messaging not initialized');
    return () => {};
  }
  
  return onMessage(messaging, (payload) => {
    console.log('[Push] Foreground message:', payload);
    
    // Show notification using Notification API
    if (Notification.permission === 'granted') {
      const notificationTitle = payload.notification?.title || 'Hayat Family';
      const notificationOptions = {
        body: payload.notification?.body || '',
        icon: payload.notification?.icon || '/icon-192.png',
        badge: '/icon-192.png',
        data: payload.data || {}
      };
      
      new Notification(notificationTitle, notificationOptions);
    }
    
    if (callback) {
      callback(payload);
    }
  });
}

/**
 * Save push token to backend
 * 
 * @param {string} token - FCM token
 * @param {string} authToken - User auth token
 */
export async function savePushToken(token, authToken) {
  try {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${BACKEND_URL}/api/family/push_token`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ push_token: token })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to save push token');
    }
    
    const data = await response.json();
    console.log('[Push] Token saved to backend:', data);
    return data;
  } catch (error) {
    console.error('[Push] Save token error:', error);
    throw error;
  }
}

/**
 * Complete Push Setup Flow
 * 
 * @param {string} authToken - User auth token
 * @returns {Promise<string>} Push token
 */
export async function setupPushNotifications(authToken) {
  try {
    // 1. Check support
    if (!isPushSupported()) {
      throw new Error('Push notifications not supported');
    }
    
    // 2. Initialize Firebase
    initializeFirebase();
    
    // 3. Get token
    const token = await getPushToken();
    
    // 4. Save to backend
    await savePushToken(token, authToken);
    
    return token;
  } catch (error) {
    console.error('[Push] Setup error:', error);
    throw error;
  }
}
